import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { setViewAsUserId } from "@/services/viewAsUser";

/**
 * The end user a SarasAdmin is currently reading dashboards as. Only the fields
 * the banner/picker render — this is UI state, not a user record.
 */
export interface ViewAsUser {
  userId: number;
  name: string;
  email: string;
  companyName?: string;
  /**
   * The target's own company id. Not rendered — it exists so an impersonated
   * cache purge can scope to the impersonated user's company rather than the
   * admin's (see `useDashboardRefreshActions`).
   */
  companyId?: number;
}

interface ViewAsUserContextValue {
  /** `null` when the admin is browsing as themselves (the normal case). */
  viewAsUser: ViewAsUser | null;
  /** Convenience mirror of `viewAsUser?.userId ?? null` — what query keys fork on. */
  viewAsUserId: number | null;
  setViewAsUser: (user: ViewAsUser) => void;
  clearViewAsUser: () => void;
}

const DEFAULT_VALUE: ViewAsUserContextValue = {
  viewAsUser: null,
  viewAsUserId: null,
  setViewAsUser: () => {},
  clearViewAsUser: () => {},
};

/**
 * Defaults to "inactive" rather than `undefined` on purpose: dashboard internals
 * (the data provider, `useDashboardQuery`) read this to fork their cache keys, and
 * they are routinely mounted in tests and in the creation preview without the
 * app-level provider. A throwing hook there would turn an admin-only feature into
 * a crash on every such mount; "nobody is being impersonated" is the correct and
 * safe reading.
 */
const ViewAsUserContext = createContext<ViewAsUserContextValue>(DEFAULT_VALUE);

/**
 * Holds the "View as user" (doAs) selection for the session and mirrors it into
 * the synchronous transport store (`services/viewAsUser`) that the axios
 * interceptor and the SSE fetch read.
 *
 * In-memory only — the mode is intentionally lost on reload. Mounted once in
 * `App.tsx`, above the route tree, so the dashboard views and the header control
 * share one selection.
 */
export const ViewAsUserProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [viewAsUser, setViewAsUserState] = useState<ViewAsUser | null>(null);

  // Reconciliation for the transport store. It is NOT the primary writer (the
  // setters below are): react-query starts the re-keyed fetch during the render
  // that follows the state change, long before a passive effect runs, so an
  // effect-only mirror would send the first request of a new identity with the
  // OLD one attached. What this effect owns is keeping the store honest if the
  // state ever changes by some path other than the setters.
  //
  // NO cleanup, deliberately: cleanups run before child effects, so a cleanup
  // that nulled the store left it null while `DashboardDataProvider` fired its
  // execute — that batch went out with no header, and the ADMIN's rows rendered
  // under the impersonation pill. Unmount teardown is the effect below.
  useEffect(() => {
    setViewAsUserId(viewAsUser?.userId ?? null);
  }, [viewAsUser]);

  // Teardown only. Empty deps, so this cleanup runs on unmount and never on a
  // change of selection: unmounting the provider disarms impersonation instead
  // of leaving a stale id on every later request.
  useEffect(() => () => setViewAsUserId(null), []);

  // Arm/disarm the transport store SYNCHRONOUSLY, before the state update. The
  // render triggered by `setViewAsUserState` re-keys the dashboard queries, and
  // react-query fires those fetches during that render — the store has to already
  // hold the new identity by then, or the admin's data lands in the impersonated
  // cache entry (and vice versa on exit).
  const setViewAsUser = useCallback((user: ViewAsUser) => {
    setViewAsUserId(user.userId);
    setViewAsUserState(user);
  }, []);
  const clearViewAsUser = useCallback(() => {
    setViewAsUserId(null);
    setViewAsUserState(null);
  }, []);

  const value = useMemo<ViewAsUserContextValue>(
    () => ({
      viewAsUser,
      viewAsUserId: viewAsUser?.userId ?? null,
      setViewAsUser,
      clearViewAsUser,
    }),
    [viewAsUser, setViewAsUser, clearViewAsUser],
  );

  return <ViewAsUserContext.Provider value={value}>{children}</ViewAsUserContext.Provider>;
};

/** The full "View as user" state + setters. Safe outside the provider (inactive). */
export function useViewAsUser(): ViewAsUserContextValue {
  return useContext(ViewAsUserContext);
}

/**
 * Just the impersonated user id — what react-query keys fork on so an admin's own
 * data and an impersonated read never share a cache entry.
 */
export function useViewAsUserId(): number | null {
  return useContext(ViewAsUserContext).viewAsUserId;
}

export default ViewAsUserContext;
